import {
  AimOutlined,
  BarChartOutlined,
  CloudServerOutlined,
  DatabaseOutlined,
  PlusOutlined,
  VideoCameraOutlined
} from "@ant-design/icons";
import { memo, useState } from "react";
import KuikaDropdownMenu, {
  KuikaDropdownMenuItemType
} from "../../../../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import { getValidTargetNodes } from "../VisionConnectionRules";
import styles from "./ExtendingConnector.module.css";

interface ExtendingConnectorProps {
  nodeId: string;
  nodeType: string;
  onNodeSelect: (nodeType: string) => void;
  compact?: boolean; // When true, shows button on handle instead of extended
}

const ExtendingConnector = ({ nodeId, nodeType, onNodeSelect, compact = false }: ExtendingConnectorProps) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // Get valid target nodes for this source node type
  const validTargets = getValidTargetNodes(nodeType);

  // Group options by category - use group property instead of dividers
  const groupedOptions: KuikaDropdownMenuItemType[] = [];

  validTargets.forEach((target) => {
    // Add the node option with group for categorization
    groupedOptions.push({
      key: target.nodeType,
      label: target.displayName,
      group: target.category,
      icon: getNodeIcon(target.nodeType),
      onClick: () => {
        onNodeSelect(target.nodeType);
        setDropdownOpen(false);
      }
    });
  });

  // Helper function to get appropriate icon for node type
  function getNodeIcon(nodeType: string): React.ReactNode {
    const iconMap: Record<string, React.ReactNode> = {
      videoInput: <VideoCameraOutlined />,
      detector: <AimOutlined />,
      tracker: <AimOutlined />,
      segmentation: <AimOutlined />,
      analyticsProcessor: <BarChartOutlined />,
      metricsCalculator: <BarChartOutlined />,
      dashboardOutput: <BarChartOutlined />,
      reportGenerator: <BarChartOutlined />,
      databaseService: <DatabaseOutlined />,
      apiGateway: <CloudServerOutlined />
    };

    return iconMap[nodeType] || <AimOutlined />;
  }

  const handleAddClick = () => {
    if (validTargets.length === 0) {
      // No valid connections - could show a message
      console.log("No valid connections available for", nodeType);
      return;
    }
    setDropdownOpen(!dropdownOpen);
  };

  return (
    <div className={`${styles.extendingConnector} ${compact ? styles.compact : ""}`}>
      {!compact && <div className={styles.connectorLine} />}
      <KuikaDropdownMenu
        options={groupedOptions}
        open={dropdownOpen}
        setOpen={setDropdownOpen}
        placement="bottomLeft"
        trigger="click"
        emptyState="No compatible nodes available"
        emptyStateCondition={validTargets.length === 0}
      >
        <button
          className={styles.addButton}
          onClick={(e) => {
            e.stopPropagation();
            handleAddClick();
          }}
          title={validTargets.length > 0 ? "Add compatible node" : "No compatible nodes available"}
          disabled={validTargets.length === 0}
        >
          <PlusOutlined />
        </button>
      </KuikaDropdownMenu>
    </div>
  );
};

export default memo(ExtendingConnector);
